import type { StateContext } from '@ngxs/store';
import { produce, type WritableDraft } from 'immer';
import { catchError, forkJoin, tap, throwError } from 'rxjs';
import type { CommentApiService } from '../../../../services/api/comments.api.service';
import type { CommentModel } from '../../../../services/api/models/CommentModel';
import * as a from '../actions';
import type { MultiViewStateModel } from '../MultiViewStateModel';
import { getViewIndex } from '.';

export namespace CommentHandler {
  /**
   * update comment via service
   * @param ctx passed State Context
   * @param param1
   * @param commentService a passed comment service to communicate with the api
   */
  export function update(ctx: StateContext<MultiViewStateModel>, { comment }: a.UpdateComment, commentService: CommentApiService) {
    commentService.updateComment(comment).subscribe(({ updatedComment }) => {
      const viewIndex = getViewIndex(ctx);
      ctx.setState(
        produce((draft) => {
          const commentIndex = draft.views[viewIndex].comments.findIndex((c) => c.identifier === updatedComment.identifier);
          if (commentIndex === -1) {
            draft.views[viewIndex].comments.push(updatedComment as unknown as CommentModel);
          } else {
            draft.views[viewIndex].comments[commentIndex] = updatedComment as unknown as CommentModel;
          }
        })
      );

      ctx.dispatch(a.CommentCallSUCCESS);
    });
  }
  /**
   * creates comment via service
   * @param ctx passed State Context
   * @param param1
   * @param commentService a passed comment service to communicate with the api
   */
  export function create(ctx: StateContext<MultiViewStateModel>, { comment }: a.CreateComment, commentService: CommentApiService) {
    const index = getViewIndex(ctx);
    commentService.createComment(comment).subscribe((comment) => {
      ctx.setState(
        produce((draft) => {
          draft.views[index].comments.push(comment);
        })
      );
      ctx.dispatch(a.CommentCallSUCCESS);
    });
  }
  /**
   * remove Comment
   * @param ctx passed State Context
   * @param param1
   * @param commentService a passed comment service to communicate with the api
   */
  export function remove(ctx: StateContext<MultiViewStateModel>, { id }: a.DeleteComment, commentService: CommentApiService) {
    const viewindex = getViewIndex(ctx);

    commentService.deleteComment(id).subscribe(() => {
      ctx.setState(
        produce((draft) => {
          draft.views[viewindex].comments = draft.views[viewindex].comments.filter((c) => c.identifier !== id);
        })
      );
      ctx.dispatch(a.CommentCallSUCCESS);
    });
  }
  /**
   *
   * @param ctx passed State Context
   * @param param1
   */
  export function toggleOnLane(ctx: StateContext<MultiViewStateModel>, { comment }: a.ToggleCommentShowLane) {
    const payload: CommentModel = {
      ...comment,
      showInLane: !comment.showInLane,
      mappedChannels: comment.mappedChannels.map((d) => d?.id) as string[],
      mappedLabels: comment.mappedLabels.map((d) => d?.identifier),
    };
    ctx.dispatch(new a.UpdateComment(payload));
  }

  /**
   * Toggle comment show in lane locally without backend call
   * @param ctx passed State Context
   * @param param1
   */
  export function toggleOnLaneLocal(
    ctx: StateContext<MultiViewStateModel>,
    { commentId, skipEcharts }: a.ToggleCommentShowLaneLocal,
    commentService: CommentApiService
  ) {
    const viewIndex = getViewIndex(ctx);
    ctx.setState(
      produce((draft) => {
        const commentIndex = draft.views[viewIndex].comments.findIndex((c) => c.identifier === commentId);
        if (commentIndex !== -1) {
          draft.views[viewIndex].comments[commentIndex].showInLane = !draft.views[viewIndex].comments[commentIndex].showInLane;
          commentService.setLocalVisibility(commentId, draft.views[viewIndex].comments[commentIndex].showInLane);
        }
      })
    );
    if (!skipEcharts) {
      ctx.dispatch(a.GenerateEChartConfig);
    }
  }
  /**
   *
   * @param ctx passed State Context
   * @param commentService a passed comment service to communicate with the api
   * @returns
   */
  export function refreshAllComments(ctx: StateContext<MultiViewStateModel>, commentService: CommentApiService) {
    const viewIndex = getViewIndex(ctx);
    const nodes = ctx.getState().views[viewIndex].sensorTable;

    if (!nodes || nodes.length === 0) {
      ctx.setState(
        produce((draft) => {
          draft.views[viewIndex].comments = [];
        })
      );
      return;
    }

    // Clear existing comments and set loading state
    ctx.setState(
      produce((draft) => {
        draft.views[viewIndex].comments = [];
        draft.views[viewIndex].isLoadingComments = true;
      })
    );

    return forkJoin(
      nodes.map((node) => {
        return commentService.getCommentsOfOneChannel({ channelId: node.id }).pipe(
          tap((comments) => {
            if (!comments?.length) {
              return;
            }
            ctx.setState(
              produce((draft) => {
                comments.forEach(upsertComment(draft, viewIndex));
              })
            );
          })
        );
      })
    ).pipe(
      tap(() => {
        // Clear loading state and generate config
        ctx.setState(
          produce((draft) => {
            draft.views[viewIndex].isLoadingComments = false;
          })
        );
        ctx.dispatch(a.GenerateEChartConfig);
      }),
      catchError((err) => {
        ctx.setState(
          produce((draft) => {
            draft.views[viewIndex].isLoadingComments = false;
          })
        );
        return throwError(() => err);
      })
    );
  }
  /**
   *
   * @param ctx passed State Context
   * @param param1
   * @param commentService a passed comment service to communicate with the api
   * @returns
   */
  export function getChannelComments(ctx: StateContext<MultiViewStateModel>, { channelId }: a.GetChannelComments, commentService: CommentApiService) {
    return commentService.getCommentsOfOneChannel({ channelId }).pipe(
      tap((comments) => {
        if (!comments) {
          return;
        }
        updateCommentsInView(ctx, comments);
      }),
      tap(() => ctx.dispatch(a.GenerateEChartConfig))
    );
  }

  /**
   * Helper to update or insert comments in the current view.
   */
  function updateCommentsInView(ctx: StateContext<MultiViewStateModel>, comments: CommentModel[]) {
    ctx.setState(
      produce((draft) => {
        const i = getViewIndex(ctx);
        comments.forEach(upsertComment(draft, i));
      })
    );
  }

  function upsertComment(draft: WritableDraft<Readonly<MultiViewStateModel>>, i: number): (value: CommentModel, index: number, array: CommentModel[]) => void {
    return (c) => {
      const index = draft.views[i].comments.findIndex((co) => co.identifier === c.identifier);
      if (index === -1) {
        draft.views[i].comments.push(c);
      } else {
        draft.views[i].comments[index] = c;
      }
    };
  }
}
